import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { useAnchorProposals, useDriveSources, useDrives, useRemoveDrive } from '@/hooks';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import type { DriveSafe } from '@vivport/backend/types';
import { ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { AddDriveDialog } from './drives/AddDriveDialog';
import { DriveRootsPanel } from './drives/DriveRootsPanel';
import { SOURCE_KIND_META } from './drives/source-kind-meta';

type DrivesSectionProps = {
  memberId: number;
  sourceId: number;
};

export function DrivesSection({ memberId, sourceId }: DrivesSectionProps) {
  const drivesQuery = useDrives(memberId);
  const sourcesQuery = useDriveSources(memberId);
  const source = (sourcesQuery.data?.sources ?? []).find((row) => row.id === sourceId) ?? null;
  const removeDrive = useRemoveDrive();
  const [addDriveOpen, setAddDriveOpen] = useState(false);
  const proposalsQuery = useAnchorProposals(undefined, memberId > 0);
  const pendingProposalCount = proposalsQuery.data?.pendingCount ?? 0;

  const handleRemoveDrive = (drive: DriveSafe) => {
    if (!window.confirm(`Remove drive ${drive.name} and its ${drive.roots.length} root(s) from this Hub?`)) {
      return;
    }
    removeDrive.mutate(drive.id, {
      onSuccess: () => toast.success(`Removed ${drive.name}.`),
      onError: (error: unknown) => toast.error(getAxiosErrorDisplayMessage(error, 'Failed to remove the drive.')),
    });
  };

  const drives = (drivesQuery.data?.drives ?? []).filter((drive) => drive.sourceId === sourceId);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="font-medium">{source?.name ?? 'Drives'}</h3>
          {source ? <span className="text-xs text-muted-foreground">{SOURCE_KIND_META[source.kind].label}</span> : null}
          {pendingProposalCount > 0 ? <Badge variant="secondary">{pendingProposalCount} pending</Badge> : null}
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => setAddDriveOpen(true)}>
          Add drive
        </Button>
      </div>

      {drivesQuery.isLoading ? <p className="text-sm text-muted-foreground">Loading drives...</p> : null}
      {drivesQuery.isError ? (
        <p className="text-sm text-destructive">{getAxiosErrorDisplayMessage(drivesQuery.error, 'Failed to load drives.')}</p>
      ) : null}
      {!drivesQuery.isLoading && !drivesQuery.isError && drives.length === 0 ? (
        <p className="text-sm text-muted-foreground">No drives yet.</p>
      ) : null}

      {drives.map((drive) => (
        <Card key={drive.id}>
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="font-medium">{drive.name}</span>
                {drive.webUrl ? (
                  <a
                    href={drive.webUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-muted-foreground hover:text-foreground"
                    aria-label={`Open ${drive.name}`}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                ) : null}
              </div>
              <p className="text-xs text-muted-foreground">
                {drive.productLabel} · {drive.roots.length} root{drive.roots.length === 1 ? '' : 's'}
              </p>
            </div>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={() => handleRemoveDrive(drive)}
              disabled={removeDrive.isPending}
            >
              Remove
            </Button>
          </CardHeader>
          <CardContent>
            <DriveRootsPanel drive={drive} />
          </CardContent>
        </Card>
      ))}

      <AddDriveDialog memberId={memberId} sourceId={sourceId} open={addDriveOpen} onOpenChange={setAddDriveOpen} />
    </div>
  );
}
